import { apiClient } from "./apiClient";
import type { LoginResponse, RegisterRequest, RegisterResponse, VerifyEmailResponse } from "../models/RegisterRequest";
import type { FunnelModuleDescriptionResponse, OnboardingRequest, OnboardingResponse, OnboardingStatusResponse } from "../models/Onboarding";
import type { FeatureFlagResponse } from "../models/FeatureFlag";

/**
 * Zentrale Schnittstelle zum Backend.
 */
export const backendApi = {
  // ==========================================
  // ✅ Authentification
  // ==========================================

  //Sendet die Registrierungs-Daten an das Backend.
  register: (data: RegisterRequest): Promise<RegisterResponse> => {
    return apiClient.post<RegisterResponse>("/api/auth/register", data);
  },

  //Verifiziert die E-Mail-Adresse anhand des Tokens aus der Verifizierungs-E-Mail.
  verifyEmail: (token: string): Promise<VerifyEmailResponse> => {
    return apiClient.get<VerifyEmailResponse>(
      `/api/auth/verify-email?token=${encodeURIComponent(token)}`,
    );
  },

  //Meldet einen User an und setzt das JWT-Cookie.
  login: (loginIdentifier: string, password: string): Promise<LoginResponse> => {
    return apiClient.post<LoginResponse>("/api/auth/login", {
      loginIdentifier,
      password,
    });
  },

  //Ruft den aktuell eingeloggten User ab.
  me: (): Promise<{
    userName: string;
    userId: string;
    displayName: string;
    email: string;
  }> => {
    return apiClient.get<{
      userName: string;
      userId: string;
      displayName: string;
      email: string;
    }>("/api/auth/me");
  },

  //Meldet den aktuellen User ab (loescht das JWT-Cookie).
  logout: (): Promise<{ message: string }> => {
    return apiClient.post<{ message: string }>("/api/auth/logout");
  },

  // ==========================================
  // ✅ AI Assistant (KI-gestuetztes Onboarding)
  // ==========================================

  /**
   * Sendet den Onboarding-Freitext an das Backend.
   */
  submitOnboarding: (freeText: string): Promise<OnboardingResponse> => {
    const request: OnboardingRequest = { freeText };
    return apiClient.post<OnboardingResponse>("/api/ai/onboarding", request);
  },

  /**
   * Liefert den Onboarding-Status des aktuellen Users.
   */
  getOnboardingStatus: (): Promise<OnboardingStatusResponse> => {
    return apiClient.get<OnboardingStatusResponse>("/api/ai/onboarding-status");
  },

  /**
   * Liefert eine personalisierte Beschreibung fuer ein Modul.
   */
  getModuleDescription: (moduleId: string): Promise<FunnelModuleDescriptionResponse> => {
    return apiClient.get<FunnelModuleDescriptionResponse>(
      `/api/ai/module-description/${encodeURIComponent(moduleId)}`,
    );
  },

  /**
   * Liefert alle Feature-Flags des aktuell eingeloggten Tenants.
   * Gibt an, welche Module freigeschaltet (enabled=true) oder gesperrt (enabled=false) sind.
   */
  getFeatureFlags: (): Promise<FeatureFlagResponse[]> => {
    return apiClient.get<FeatureFlagResponse[]>("/api/feature-flags");
  },
};